import type { AdminSelf } from '@saay/api-client';
import {
  formatCount,
  formatRatePercent,
  formatToman,
  formatTomanCompact,
  statusLabel,
} from '@saay/formatting';
import type { components } from '@saay/api-client';
import { IconTile, StatusBadge, type Tone } from '@saay/blocks';
import { buttonVariants } from '@saay/shadcn/components/button';
import { Progress } from '@saay/shadcn/components/progress';
import { cn } from '@saay/shadcn/lib/utils';
import { ErrorState, PageHeader, useWindowClass } from '../kit.js';
import { PlusIcon, StoreIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';

import type { AdminMerchantSummary } from '../api.js';
import { AdminListTable } from '../components/admin-list-table.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { useShellCenterAction } from '../components/admin-shell.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { ListLoadMore, ListFooter, totalEyebrow } from '../components/list-footer.js';
import { ListSearchInput } from '../components/list-filters.js';
import { ClearFiltersButton, ListEmpty, ListSkeleton } from '../components/list-state.js';
import { OPENED_FROM_LIST } from '../components/route-sheet.js';
import { OUTLINE_PILL, SurfaceSection } from '../components/surface-section.js';
import { merchantSearchFromText } from '../merchant-search.js';
import { routeFor } from '../nav.js';
import { canPerform } from '../permissions.js';
import {
  firstPageTotal,
  listStatus,
  type MerchantFilters,
  useCategories,
  useCities,
  useMerchants,
  useMerchantsSummary,
  useProvinces,
} from '../queries.js';

const FILTER_KEYS = [
  'status',
  'q',
  'contactMobileE164',
  'provinceId',
  'cityId',
  'primaryCategoryId',
  'sort',
] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

type MerchantCounts = components['schemas']['AdminMerchantsSummary']['merchantCountsByStatus'];

const STATUS_FILTER_OPTIONS = [
  { key: 'all', status: '' },
  { key: 'ACTIVE', status: 'ACTIVE' },
  { key: 'PENDING_ACTIVATION', status: 'PENDING_ACTIVATION' },
  { key: 'SUSPENDED', status: 'SUSPENDED' },
] as const;

const ZERO_MERCHANT_STATUS_COUNTS: MerchantCounts = {
  ACTIVE: 0,
  CLOSED: 0,
  PENDING_ACTIVATION: 0,
  SUSPENDED: 0,
};

export interface MerchantsOutletContext {
  readonly self: AdminSelf;
}

function readFilters(params: URLSearchParams): MerchantFilters {
  const filters: Record<string, string> = {};
  for (const key of FILTER_KEYS) {
    const value = params.get(key);
    if (value !== null && value !== '') {
      filters[key] = value;
    }
  }
  return filters as MerchantFilters;
}

function merchantStatusOptions(counts?: MerchantCounts) {
  return STATUS_FILTER_OPTIONS.filter((option) => option.key !== 'all').map((option) => {
    const count = counts?.[option.key as keyof MerchantCounts];
    return {
      label: `${statusLabel('merchantStatus', option.key)}${count === undefined ? '' : ` ${formatCount(count)}`}`,
      value: option.status,
    };
  });
}

/** The v2 tone of a merchant status (D-93): active olive, waiting clay, suspended copper. */
export const MERCHANT_STATUS_TONE: Readonly<Record<string, Tone>> = {
  ACTIVE: 'olive',
  CLOSED: 'forest',
  PENDING_ACTIVATION: 'clay',
  SUSPENDED: 'copper',
};

function MerchantStatusBadge({ value }: { readonly value: string }) {
  if (value === 'ACTIVE') {
    return null;
  }
  return (
    <StatusBadge tone={MERCHANT_STATUS_TONE[value] ?? 'forest'}>
      {statusLabel('merchantStatus', value)}
    </StatusBadge>
  );
}

function capacityPercent(rate: string | null): number {
  const value = Number(rate ?? '0');
  return Number.isFinite(value) ? Math.min(100, Math.max(0, value * 100)) : 0;
}

/**
 * Search, status counts from the server, and immediate geography and category filters (AC §4.8).
 */
function MerchantToolbar({
  counts,
  filters,
  onChange,
  onClearAll,
}: {
  readonly counts?: MerchantCounts;
  readonly filters: MerchantFilters;
  readonly onChange: (changes: Partial<Record<FilterKey, string>>, clearAll?: boolean) => void;
  readonly onClearAll: () => void;
}) {
  const provinces = useProvinces();
  const cities = useCities(filters.provinceId === '' ? undefined : filters.provinceId);
  const categories = useCategories('ACTIVE');
  const appliedSearch = filters.contactMobileE164 ?? filters.q ?? '';
  const [searchText, setSearchText] = useState(appliedSearch);
  const searchMessage = useMemo(() => {
    if (searchText.trim() === '') return undefined;
    const parsed = merchantSearchFromText(searchText);
    return 'message' in parsed ? parsed.message : undefined;
  }, [searchText]);

  function applySearch() {
    const parsed = merchantSearchFromText(searchText);
    if ('message' in parsed) return;
    onChange({ contactMobileE164: '', q: '', ...parsed.filters });
  }

  return (
    <div>
      <FilterPillBar
        activeFilterCount={
          Object.keys(filters).filter((key) => key !== 'sort').length +
          ((filters.sort ?? '-createdAt') === '-createdAt' ? 0 : 1)
        }
        label="فیلتر فروشگاه‌ها"
        onClearAll={onClearAll}
        sort={
          <>
            <FilterPill
              emptyLabel="جدیدترین"
              inactiveLabel="جدیدترین"
              label="مرتب‌سازی"
              onChange={(sort) => onChange({ sort })}
              options={[{ label: 'نام فروشگاه', value: 'displayNameFa' }]}
              value={(filters.sort ?? '-createdAt') === '-createdAt' ? '' : (filters.sort ?? '')}
            />
            {routeFor('/exports')?.ready === true ? (
              <Link className={buttonVariants({ size: 'sm', variant: 'outline' })} to="/exports">
                خروجی
              </Link>
            ) : null}
          </>
        }
      >
        <form
          className="shrink-0"
          onSubmit={(event) => {
            event.preventDefault();
            applySearch();
          }}
        >
          <ListSearchInput
            invalid={searchMessage !== undefined}
            label="جست‌وجوی فروشگاه"
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="نام، شناسهٔ ملی یا شماره موبایل"
            value={searchText}
          />
        </form>
        <FilterPill
          emptyLabel="همه وضعیت‌ها"
          label="وضعیت فروشگاه"
          onChange={(status) => onChange({ status })}
          options={merchantStatusOptions(counts)}
          value={filters.status ?? ''}
        />
        <FilterPill
          emptyLabel="همه استان‌ها"
          label="استان"
          onChange={(provinceId) => onChange({ cityId: '', provinceId })}
          options={(provinces.data ?? []).map((item) => ({ label: item.nameFa, value: item.id }))}
          value={filters.provinceId ?? ''}
        />
        <FilterPill
          disabled={!filters.provinceId || cities.status !== 'success'}
          emptyLabel="همه شهرها"
          label="شهر"
          onChange={(cityId) => onChange({ cityId })}
          options={(cities.data ?? []).map((item) => ({ label: item.nameFa, value: item.id }))}
          value={filters.cityId ?? ''}
        />
        <FilterPill
          emptyLabel="همه دسته‌بندی‌ها"
          label="دسته‌بندی"
          onChange={(primaryCategoryId) => onChange({ primaryCategoryId })}
          options={(categories.data ?? []).map((item) => ({ label: item.pathFa, value: item.id }))}
          value={filters.primaryCategoryId ?? ''}
        />
      </FilterPillBar>
      {searchMessage === undefined ? null : (
        <p className="mb-3 text-sm text-destructive">{searchMessage}</p>
      )}
    </div>
  );
}

function MerchantTable({ rows }: { readonly rows: readonly AdminMerchantSummary[] }) {
  const { search } = useLocation();
  const navigate = useNavigate();
  const open = (row: AdminMerchantSummary) => ({ pathname: `/merchants/${row.id}`, search });
  return (
    <AdminListTable
      caption="فهرست فروشگاه‌ها"
      columns={[
        {
          header: 'فروشگاه',
          id: 'name',
          pin: 'start',
          render: (row) => (
            <span className="flex items-center gap-3">
              <IconTile
                icon={StoreIcon}
                size="sm"
                tone={MERCHANT_STATUS_TONE[row.status] ?? 'forest'}
              />
              <span className="saay-cell-main">
                <Link className="font-bold" state={OPENED_FROM_LIST} to={open(row)}>
                  {row.displayNameFa}
                </Link>
                <small className="text-xs">{row.legalNameFa}</small>
              </span>
            </span>
          ),
        },
        {
          header: 'شعبه',
          id: 'branches',
          numeric: true,
          render: (row) => formatCount(row.activeBranchCount),
        },
        {
          header: 'ظرفیت',
          id: 'capacity',
          render: (row) => {
            const valueText = `${formatToman(row.capacityUsage.usedCapacityIrr)} از ${formatToman(row.capacityUsage.approvedLimitIrr)}`;
            if (row.capacityUsage.approvedLimitIrr === 0) {
              return <span className="text-xs text-muted-foreground">ظرفیت ثبت نشده</span>;
            }
            return (
              <span
                className="flex min-w-40 items-center gap-3"
                data-near-limit={row.capacityUsage.nearLimit || undefined}
              >
                <Progress
                  aria-label="مصرف ظرفیت"
                  aria-valuetext={valueText}
                  className={cn(
                    'min-w-16 flex-1 [&_[data-slot=progress-track]]:h-2 [&_[data-slot=progress-track]]:bg-muted-foreground/35',
                    row.capacityUsage.nearLimit &&
                      '[&_[data-slot=progress-indicator]]:bg-tone-copper-strong',
                  )}
                  value={capacityPercent(row.capacityUsage.utilizationRate)}
                />
                <span className="shrink-0 text-xs font-bold tabular-nums">
                  {row.capacityUsage.utilizationRate === null
                    ? '۰٪'
                    : formatRatePercent(row.capacityUsage.utilizationRate, { invalid: 'raw' })}
                </span>
              </span>
            );
          },
        },
        {
          header: 'نرخ SAAY',
          id: 'rate',
          render: (row) =>
            row.saayFeeRate === null ? '—' : formatRatePercent(row.saayFeeRate, { invalid: 'raw' }),
        },
        {
          header: 'دسته‌بندی',
          id: 'category',
          render: (row) => (
            <span title={row.primaryCategory.pathFa}>{row.primaryCategory.nameFa}</span>
          ),
        },
        {
          header: 'استان / شهر',
          id: 'geo',
          render: (row) => `${row.province.nameFa} / ${row.city.nameFa}`,
        },
        {
          header: 'وضعیت',
          id: 'status',
          render: (row) => (
            <span className="inline-flex flex-wrap items-center gap-1">
              <MerchantStatusBadge value={row.status} />
              {row.capacityUsage.nearLimit ? (
                <StatusBadge tone="copper">نزدیک سقف</StatusBadge>
              ) : null}
            </span>
          ),
        },
      ]}
      mobileSummary={(row) => ({
        primary: row.displayNameFa,
        secondary: (
          <span className="inline-flex flex-wrap items-center gap-1">
            <MerchantStatusBadge value={row.status} />
            <span>
              {row.capacityUsage.approvedLimitIrr === 0
                ? 'ظرفیت ثبت نشده'
                : `ظرفیت ${formatTomanCompact(row.capacityUsage.usedCapacityIrr, { label: false })} از ${formatTomanCompact(row.capacityUsage.approvedLimitIrr)}`}
            </span>
          </span>
        ),
      })}
      onRowClick={(row) => navigate(open(row), { state: OPENED_FROM_LIST })}
      rowKey={(row) => row.id}
      rows={rows}
    />
  );
}

export function MerchantsPage() {
  const { pathname } = useLocation();
  usePageTitle('فروشگاه‌ها', pathname.replace(/\/$/, '') === '/merchants');
  return <RequireArea area="merchants">{(self) => <MerchantsView self={self} />}</RequireArea>;
}

/** Data hooks live below the gate, so a role without the area sends no list request. */
function MerchantsView({ self }: { readonly self: AdminSelf }) {
  const { pathname } = useLocation();
  const detailPage = /^\/merchants\/[^/]+$/.test(pathname);
  // Two components, so the list's hooks are not skipped (and the hook count does not change) when
  // the route moves between the list and a merchant page.
  return detailPage ? (
    <Outlet context={{ self } satisfies MerchantsOutletContext} />
  ) : (
    <MerchantsListView self={self} />
  );
}

function MerchantsListView({ self }: { readonly self: AdminSelf }) {
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const merchants = useMerchants(filters);
  const summary = useMerchantsSummary();
  const rows = merchants.data?.pages.flatMap((page) => page.items) ?? [];
  const total = firstPageTotal(merchants.data);
  const statusCounts =
    listStatus(merchants) === 'success' && rows.length === 0
      ? ZERO_MERCHANT_STATUS_COUNTS
      : summary.data?.merchantCountsByStatus;
  const filtered = Object.keys(filters).some((key) => key !== 'sort');
  const canCreate = canPerform(self, 'createMerchant');
  const windowClass = useWindowClass('expanded');
  useShellCenterAction(
    canCreate && windowClass === 'compact'
      ? { href: '/merchants/new', label: 'فروشگاه جدید', state: OPENED_FROM_LIST }
      : null,
  );

  function change(changes: Partial<Record<FilterKey, string>>, clearAll = false) {
    const next = new URLSearchParams(clearAll ? '' : params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next);
  }

  return (
    <div className="admin-merchants-page">
      <PageHeader
        actions={
          <>
            <Link
              className={cn(buttonVariants({ variant: 'outline' }), OUTLINE_PILL)}
              state={OPENED_FROM_LIST}
              to="/merchant-categories"
            >
              دسته‌بندی‌ها
            </Link>
            {canCreate && windowClass !== 'compact' ? (
              <Link
                className={buttonVariants({ className: 'rounded-full px-5' })}
                state={OPENED_FROM_LIST}
                to="/merchants/new"
              >
                <PlusIcon aria-hidden="true" />
                فروشگاه جدید
              </Link>
            ) : null}
          </>
        }
        title="فروشگاه‌ها"
      />
      <SurfaceSection
        {...(totalEyebrow(total, 'فروشگاه') === undefined
          ? {}
          : { description: totalEyebrow(total, 'فروشگاه') })}
        id="merchant-list"
        title="فروشگاه‌های ثبت‌شده"
      >
        <MerchantToolbar
          {...(statusCounts === undefined ? {} : { counts: statusCounts })}
          filters={filters}
          onChange={change}
          onClearAll={() => change({}, true)}
        />
        <QueryState
          empty={
            <ListEmpty
              action={
                filtered ? (
                  <ClearFiltersButton onClick={() => change({}, true)} />
                ) : canCreate ? (
                  <Link className={buttonVariants()} state={OPENED_FROM_LIST} to="/merchants/new">
                    ثبت اولین فروشگاه
                  </Link>
                ) : undefined
              }
              title={filtered ? 'فروشگاهی با این فیلترها پیدا نشد' : 'هنوز فروشگاهی ثبت نشده است'}
            />
          }
          error={merchants.error}
          isEmpty={rows.length === 0}
          onRetry={() => void merchants.refetch()}
          skeleton={<ListSkeleton label="در حال بارگذاری فروشگاه‌ها…" />}
          status={listStatus(merchants)}
        >
          <MerchantTable rows={rows} />
          {merchants.isFetchNextPageError ? (
            <ErrorState error={merchants.error} onRetry={() => void merchants.fetchNextPage()} />
          ) : null}
          <ListFooter
            pager={
              <ListLoadMore
                hasMore={merchants.hasNextPage}
                loading={merchants.isFetchingNextPage}
                onLoadMore={() => void merchants.fetchNextPage()}
              />
            }
            shown={rows.length}
            total={total}
          />
        </QueryState>
      </SurfaceSection>
      <Outlet context={{ self } satisfies MerchantsOutletContext} />
    </div>
  );
}
